import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box, TextField, FormControl, Select, MenuItem, Button,
  CircularProgress, IconButton, Tooltip, Chip,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { theme } from '../../theme/colors';
import { MOBILIZE_EVENTS_ENABLED } from '../../utils/featureFlags';
import { getGooglePlacesApiKey } from '../../utils/googleMapsConfig';
import { formatLocationFilterLabel } from '../../utils/locationUtils';

const GOOGLE_PLACES_API_KEY = getGooglePlacesApiKey();

const FONT = '"DM Sans", sans-serif';

const primaryBtnSx = {
  bgcolor: theme.primary,
  color: '#fff',
  fontWeight: 600,
  fontSize: 14,
  fontFamily: FONT,
  textTransform: 'none',
  borderRadius: '10px',
  px: 2,
  minHeight: 38,
  boxShadow: 'none',
  '&:hover': { bgcolor: theme.primaryHover || '#065f46', boxShadow: 'none' },
};

const chipSx = {
  fontWeight: 600,
  fontFamily: FONT,
  fontSize: 13,
  bgcolor: '#f8fafc',
  color: '#0f172a',
  border: '1px solid #e2e8f0',
  '& .MuiChip-label': { px: 1 },
  '& .MuiChip-deleteIcon': {
    color: '#0f172a',
    fontSize: 16,
    '&:hover': { color: '#0f172a' },
  },
};

const selectSx = {
  borderRadius: '10px',
  fontWeight: 600,
  fontFamily: FONT,
  fontSize: 14,
  color: '#0f172a',
  bgcolor: '#f9fafb',
  minHeight: 38,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
  '& .MuiSelect-icon': { color: '#334155', right: 8, fontSize: '1.35rem' },
};

const placesStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? theme.primary : '#e2e8f0',
    backgroundColor: '#f9fafb',
    boxShadow: 'none',
    fontFamily: FONT,
    '&:hover': { borderColor: '#cbd5e1' },
  }),
  valueContainer: (provided) => ({ ...provided, paddingLeft: 8, fontFamily: FONT }),
  placeholder: (provided) => ({ ...provided, fontSize: 14, color: '#94a3b8', fontFamily: FONT }),
  input: (provided) => ({ ...provided, fontSize: 14, color: '#0f172a', fontFamily: FONT }),
  singleValue: (provided) => ({ ...provided, fontFamily: FONT, fontSize: 14, color: '#0f172a' }),
  menu: (provided) => ({ ...provided, zIndex: 9999, borderRadius: 10, fontFamily: FONT }),
  option: (provided) => ({ ...provided, fontFamily: FONT, fontSize: 14 }),
};

function ViewToggle({ viewMode, listHref, mapHref }) {
  const btn = (active) => ({
    py: 0.75,
    px: 1.5,
    minHeight: 38,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 14,
    fontFamily: FONT,
    borderRadius: '8px',
    boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
    bgcolor: active ? '#fff' : 'transparent',
    color: active ? theme.primary : '#64748b',
    border: active ? `1px solid ${theme.primary}33` : '1px solid transparent',
    '&:hover': { bgcolor: active ? '#fff' : 'rgba(255,255,255,0.7)', color: active ? theme.primary : '#334155' },
  });

  return (
    <Box
      role="group"
      aria-label="View"
      sx={{
        display: 'inline-flex',
        p: '3px',
        borderRadius: '10px',
        bgcolor: '#f1f5f9',
        border: '1px solid #e2e8f0',
        flexShrink: 0,
      }}
    >
      {viewMode === 'list' ? (
        <Button disableElevation sx={btn(true)}>List</Button>
      ) : (
        <Button component={Link} to={listHref} disableElevation sx={btn(false)}>List</Button>
      )}
      {viewMode === 'map' ? (
        <Button disableElevation sx={btn(true)}>Map</Button>
      ) : (
        <Button component={Link} to={mapHref} disableElevation sx={btn(false)}>Map</Button>
      )}
    </Box>
  );
}

export default function EventsFiltersToolbar({
  viewMode,
  listHref,
  mapHref,
  eventTypes,
  selectedEventType,
  onEventTypeChange,
  isLoggedIn,
  rsvpFilter,
  onRsvpFilterChange,
  civixOnly,
  onCivixOnlyChange,
  showHostEventButton,
  onHostEvent,
  locationSearchInput,
  setLocationSearchInput,
  submittedLocationSearch,
  setSubmittedLocationSearch,
  setUseCurrentLocation,
  onClearAllFilters,
  sx,
  hasPlacesApiKey,
  selectedPlace,
  showResolvedLocation,
  setShowResolvedLocation,
  setSelectedPlace,
  isLocating,
  handleSearchSubmit,
  handleClearSearch,
  handlePlaceSelect,
  handleUseCurrentLocation,
}) {
  const hasLocationFilter = Boolean(submittedLocationSearch);
  const hasActiveFilters =
    hasLocationFilter ||
    selectedEventType ||
    rsvpFilter ||
    (MOBILIZE_EVENTS_ENABLED && civixOnly);

  const eventTypeLabel = selectedEventType
    ? (eventTypes.find((t) => t.name === selectedEventType)?.display_name || selectedEventType)
    : null;

  const rsvpLabel = rsvpFilter === 'interested' ? 'Interested' : rsvpFilter === 'going' ? 'Attending' : null;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '14px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        p: { xs: 1.5, sm: 2 },
        fontFamily: FONT,
        ...sx,
      }}
    >
      {/* Row 1: location */}
      <Box
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 1.5 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {hasPlacesApiKey && !showResolvedLocation ? (
            <GooglePlacesAutocomplete
              apiKey={GOOGLE_PLACES_API_KEY}
              selectProps={{
                value: selectedPlace,
                onChange: handlePlaceSelect,
                placeholder: 'City or state',
                isClearable: true,
                styles: placesStyles,
              }}
              autocompletionRequest={{
                componentRestrictions: { country: 'us' },
                types: ['(regions)'],
              }}
              debounce={250}
              withSessionToken
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              placeholder="City or state"
              value={locationSearchInput}
              onChange={(e) => {
                setUseCurrentLocation(false);
                setLocationSearchInput(e.target.value);
                setSelectedPlace(null);
                if (e.target.value === '') {
                  setShowResolvedLocation(false);
                  setSubmittedLocationSearch('');
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MyLocationIcon sx={{ fontSize: 16, color: theme.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#f9fafb',
                  fontSize: 14,
                  fontFamily: FONT,
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                },
              }}
            />
          )}
        </Box>
        <Tooltip title={isLoggedIn ? 'Use my location' : 'Sign in required'}>
          <span>
            <IconButton
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={isLocating || !isLoggedIn}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                bgcolor: '#fff',
                width: 38,
                height: 38,
                '&:hover': { borderColor: theme.primary, color: theme.primary, bgcolor: '#f0fdf4' },
              }}
            >
              {isLocating ? <CircularProgress size={16} sx={{ color: theme.primary }} /> : <MyLocationIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
        <Button type="submit" variant="contained" sx={primaryBtnSx}>
          Search
        </Button>
      </Box>

      {/* Row 2: filters + view + CTA */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={selectedEventType}
            onChange={(e) => onEventTypeChange(e.target.value)}
            displayEmpty
            IconComponent={KeyboardArrowDownIcon}
            sx={selectSx}
          >
            <MenuItem value="">All types</MenuItem>
            {eventTypes.map((t) => (
              <MenuItem key={t.id || t.name} value={t.name}>
                {t.display_name || t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isLoggedIn && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={rsvpFilter}
              onChange={(e) => onRsvpFilterChange(e.target.value)}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              sx={selectSx}
              renderValue={(v) => {
                if (v === 'interested') return 'Interested';
                if (v === 'going') return 'Attending';
                return 'My events';
              }}
            >
              <MenuItem value="">All events</MenuItem>
              <MenuItem value="interested">Interested</MenuItem>
              <MenuItem value="going">Attending</MenuItem>
            </Select>
          </FormControl>
        )}

        {MOBILIZE_EVENTS_ENABLED && (
          <Button
            onClick={() => onCivixOnlyChange(!civixOnly)}
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              fontFamily: FONT,
              minHeight: 38,
              px: 1.5,
              borderRadius: '10px',
              color: civixOnly ? theme.primary : '#64748b',
              bgcolor: civixOnly ? `${theme.primary}10` : '#f9fafb',
              border: civixOnly ? `1px solid ${theme.primary}` : '1px solid #e2e8f0',
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: civixOnly ? `${theme.primary}15` : '#f1f5f9',
              },
            }}
            aria-pressed={civixOnly}
            aria-label={civixOnly ? 'Showing CivixThread events only' : 'Show CivixThread events only'}
          >
            Hosted by CivixThread
          </Button>
        )}

        <ViewToggle viewMode={viewMode} listHref={listHref} mapHref={mapHref} />

        {showHostEventButton && (
          <Button
            onClick={onHostEvent}
            variant="contained"
            sx={{ ...primaryBtnSx, ml: { sm: 'auto' } }}
          >
            Create event
          </Button>
        )}
      </Box>

      {/* Active filters */}
      {hasActiveFilters && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mt: 1.25 }}>
          {hasLocationFilter && (
            <Chip
              label={formatLocationFilterLabel(submittedLocationSearch)}
              size="small"
              onDelete={handleClearSearch}
              sx={chipSx}
            />
          )}
          {eventTypeLabel && (
            <Chip
              label={eventTypeLabel}
              size="small"
              onDelete={() => onEventTypeChange('')}
              sx={chipSx}
            />
          )}
          {rsvpLabel && (
            <Chip
              label={rsvpLabel}
              size="small"
              onDelete={() => onRsvpFilterChange('')}
              sx={chipSx}
            />
          )}
          {civixOnly && (
            <Chip
              label="Hosted by CivixThread"
              size="small"
              onDelete={() => onCivixOnlyChange(false)}
              sx={chipSx}
            />
          )}
          <Button
            onClick={onClearAllFilters}
            size="small"
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              fontFamily: FONT,
              color: '#0f172a',
              minWidth: 'auto',
              minHeight: 0,
              height: 24,
              px: 1.25,
              py: 0,
              borderRadius: '12px',
              border: 'none',
              boxShadow: 'inset 0 0 0 1px #0f172a',
              lineHeight: 1,
              '&:hover': {
                bgcolor: '#f8fafc',
                boxShadow: 'inset 0 0 0 1px #0f172a',
              },
            }}
          >
            Clear all
          </Button>
        </Box>
      )}
    </Box>
  );
}
